import React, {
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
  useState
} from 'react';
import { CombinedError } from 'urql';

import useBreakpoint from 'hooks/useBreakpoint';

import buildLabelSubscriberActions from './buildLabelSubscriberActions';
import useArchiveAccessMutations from './useArchiveAccessMutations';
import { ActionMenuItemProps } from 'components/ActionMenu/ActionMenuItem';
import { GetLabelAndViewerQuery, GetLabelSubscribersQuery } from 'gql/graphql';
import { ElementOf } from 'types/utils';
import { getNextSortBy } from 'components/Lists/helpers';
import { TableColumn } from 'components/Lists/TableTypes';
import {
  FilterByConfig,
  SortByConfig
} from 'components/TableFilterControls/TableFilterControls';
import { PAGINATION_CONFIGS } from 'config/constants';
import useUrlQueryParams, { UpdateQueryParams } from 'hooks/useUrlQueryParams';
import { NumberParam, createEnumParam, StringParam } from 'use-query-params';
import { getSortValue } from './helpers';
import {
  FilterSlug,
  SUBSCRIBER_FILTER_OPTIONS,
  DEFAULT_FILTER,
  DEFAULT_SORT
} from './LabelSubscriberOptions';

export type LabelSubscription = ElementOf<
  NonNullable<NonNullable<GetLabelSubscribersQuery['label']>['subscribers']>['edges']
>;
export type LabelSubscriber = LabelSubscription['node'];

type SubscribersPageInfo = NonNullable<
  NonNullable<GetLabelSubscribersQuery['label']>['subscribers']
>['pageInfo'];

export type ConfirmationModalState = null | {
  type: 'archive-access' | 'remove-subscriber';
  artist: LabelSubscriber;
};

type LabelSubscribersParams = {
  page: number;
  perPage: number;
  filter: FilterSlug;
  filterBy: FilterByConfig;
  setSortBy: (column: TableColumn) => void;
  sortBy: SortByConfig;
  sortValue: string;
  setUrlQuery: UpdateQueryParams<typeof labelSubscribersQueryParams>;
  searchText?: string | null;
};

interface LabelSubscribersContextValue extends LabelSubscribersParams {
  confirmationModal: ConfirmationModalState;
  setConfirmationModal: React.Dispatch<SetStateAction<ConfirmationModalState>>;
  label: NonNullable<GetLabelAndViewerQuery['label']>;
  isSmallScreen: boolean;
  getSubscriberActions: (subscription: LabelSubscription) => ActionMenuItemProps[];
  fetching: boolean;
  error: CombinedError | null;
  pageInfo: SubscribersPageInfo | null;
  setFetching: (fetching: boolean) => void;
  setError: (error: CombinedError) => void;
  setPageInfo: React.Dispatch<SetStateAction<SubscribersPageInfo | null>>;
}

const LabelSubscribersContext = createContext<LabelSubscribersContextValue | null>(null);

const labelSubscribersQueryParams = {
  page: { type: NumberParam, default: 1 },
  perPage: { type: NumberParam, default: PAGINATION_CONFIGS.ITEMS_PER_PAGE_OPTIONS[0] },
  filter: {
    type: createEnumParam(SUBSCRIBER_FILTER_OPTIONS.map(option => option.value)),
    default: DEFAULT_FILTER.value
  },
  q: { type: StringParam, default: '' },
  sort: { type: StringParam, default: DEFAULT_SORT.id },
  dir: { type: createEnumParam(['ASC', 'DESC']), default: DEFAULT_SORT.direction }
};

export function LabelSubscribersProvider({
  children,
  label
}: {
  children: ReactNode;
  label: NonNullable<GetLabelAndViewerQuery['label']>;
}) {
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<CombinedError | null>(null);
  const [pageInfo, setPageInfo] = useState<SubscribersPageInfo | null>(null);
  const [confirmationModal, setConfirmationModal] =
    useState<ConfirmationModalState>(null);

  const { sort, dir, filter, q, page, perPage, updateParams } = useUrlQueryParams(
    labelSubscribersQueryParams
  );

  const sortBy = useMemo(() => ({ [sort]: dir }), [sort, dir]);
  const setSortBy = (column: TableColumn) => {
    const nextSort = getNextSortBy(column, sort, dir);
    updateParams({
      page: 1,
      sort: nextSort.id,
      dir: nextSort.direction
    });
  };
  const filterBy = useMemo(
    () => ({
      ...SUBSCRIBER_FILTER_OPTIONS.find(({ value }) => value === filter)?.filterBy,
      ...(q ? { artistName: { cont: q || null } } : {})
    }),
    [filter, q]
  );
  const sortValue = useMemo(() => getSortValue(sort, dir), [sort, dir]);

  const labelSubscribersParams = {
    setSortBy,
    sortBy,
    filter,
    filterBy,
    page,
    perPage,
    sortValue,
    setUrlQuery: updateParams,
    searchText: q
  };

  const archiveAccessApi = useArchiveAccessMutations();
  const getSubscriberActions = (subscription: LabelSubscription) =>
    buildLabelSubscriberActions(
      label,
      subscription,
      archiveAccessApi,
      setConfirmationModal
    );

  const isSmallScreen = useBreakpoint(
    useBreakpoint.BREAKPOINTS.SM,
    useBreakpoint.DIRECTIONS.DOWN
  );

  const context = {
    ...labelSubscribersParams,
    getSubscriberActions,
    confirmationModal,
    setConfirmationModal,
    label,
    isSmallScreen,
    fetching,
    error,
    setFetching,
    setError,
    pageInfo,
    setPageInfo
  };

  return (
    <LabelSubscribersContext.Provider value={context}>
      {children}
    </LabelSubscribersContext.Provider>
  );
}

export function useLabelSubscribersContext() {
  const context = useContext(LabelSubscribersContext);
  if (!context) throw 'Hook called from component with no parent provider';

  return context;
}
